import React from 'react';
import { CAMPAIGN_TYPE_MAP } from '@/utilities/constant';

type CallRow =
  | { campaign_id?: unknown; campaign_name?: unknown; campaign_type?: unknown; cdr_call_type?: unknown; call_leg?: unknown }
  | null
  | undefined;

const NOT_DIALED = /^(INBOUND|TRANSFER|MONITOR|CONFERENCE)/;
const notDialedLeg = (row: NonNullable<CallRow>) =>
  String(row.call_leg || '').toUpperCase() !== 'A_PARTY' && NOT_DIALED.test(String(row.cdr_call_type || '').toUpperCase());

export const isCampaignRow = (row: CallRow) => Number(row?.campaign_id) > 0;

export const campaignLabel = (row: CallRow) =>
  isCampaignRow(row) ? String(row?.campaign_name || `#${row?.campaign_id}`) : '';

const campaignDetail = (row: CallRow) => {
  const type = (CAMPAIGN_TYPE_MAP as Record<string, string>)[String(row?.campaign_type)];
  const name = row?.campaign_name ? ` ${row.campaign_name}` : '';
  return `#${row?.campaign_id}${name}${type ? `, ${type}` : ''}`;
};

export const callModeLabel = (row: CallRow, isOutgoing: boolean) => {
  if (!isOutgoing || !row) return '';
  if (isCampaignRow(row)) return `Auto Dial (${campaignDetail(row)})`;
  return notDialedLeg(row) ? '' : 'Manual Dial';
};

export const withCallMode = (value: unknown, row: CallRow, isOutgoing: boolean) =>
  [value, callModeLabel(row, isOutgoing)].filter((v) => v != null && v !== '').join(', ');

function CallModeTag({ row, isOutgoing }: { row: CallRow; isOutgoing: boolean }) {
  const label = callModeLabel(row, isOutgoing);
  if (!label) return null;
  const pill = 'inline-flex w-fit items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ';
  if (!isCampaignRow(row)) {
    return (
      <span className={pill + 'bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800/60 dark:text-slate-300 dark:ring-slate-700'}>
        {label}
      </span>
    );
  }
  return (
    <span title={label} className="flex max-w-[200px] flex-col items-start gap-0.5">
      <span className={pill + 'bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900'}>
        Auto Dial
      </span>
      <span className="break-words text-[11px] leading-tight text-muted-foreground">{campaignDetail(row)}</span>
    </span>
  );
}

export default React.memo(CallModeTag);
